export type CsrTheme = 'navy' | 'green' | 'purple' | 'default';

export const CSR_THEMES: { id: CsrTheme; label: string; color: string; sub: string }[] = [
  { id: 'navy',    label: 'Dark Navy',       color: '#4f8ef7', sub: 'Default'       },
  { id: 'green',   label: 'Bottle Green',    color: '#00c272', sub: 'Forest'        },
  { id: 'purple',  label: 'Midnight Purple', color: '#a855f7', sub: 'Violet'        },
  { id: 'default', label: 'Website Default', color: '#04a251', sub: 'Classic Light' },
];

export const CSR_THEME_VARS: Record<CsrTheme, Record<string, string>> = {
  navy: {
    '--csr-bg':          '#07101f',
    '--csr-surface':     '#0d1b3e',
    '--csr-surface2':    '#112040',
    '--csr-panel':       '#0d1b3e',
    '--csr-input':       '#112040',
    '--csr-border':      '#1e3055',
    '--csr-border2':     '#162238',
    '--csr-text':        '#e8edf5',
    '--csr-text2':       '#7b8fa6',
    '--csr-text3':       '#3d5068',
    '--csr-muted':       '#7b8fa6',
    '--csr-accent':      '#4f8ef7',
    '--csr-accent-h':    '#6ba3ff',
    '--csr-accent-glow': 'rgba(79,142,247,0.15)',
    '--csr-topbar':      '#050e1c',
    '--csr-sidebar-bg':          '#050e1c',
    '--csr-sidebar-heading':     '#4f8ef7',
    '--csr-sidebar-text':        '#e8edf5',
    '--csr-sidebar-active-bg':   '#4f8ef7',
    '--csr-sidebar-active-text': '#ffffff',
    '--csr-sidebar-hover-text':  '#4f8ef7',
    '--csr-sidebar-submenu-bg':  '#0d1b3e',
    '--csr-header-logo-filter':  'brightness(0) invert(1)',
    '--csr-sidebar-icon-filter': 'brightness(0) invert(1)',
  },
  green: {
    '--csr-bg':          '#030e06',
    '--csr-surface':     '#061509',
    '--csr-surface2':    '#091d0e',
    '--csr-panel':       '#061509',
    '--csr-input':       '#091d0e',
    '--csr-border':      '#143d1e',
    '--csr-border2':     '#0b2010',
    '--csr-text':        '#e4f5ec',
    '--csr-text2':       '#76a88a',
    '--csr-text3':       '#3a5c48',
    '--csr-muted':       '#76a88a',
    '--csr-accent':      '#00c272',
    '--csr-accent-h':    '#1ae88a',
    '--csr-accent-glow': 'rgba(0,194,114,0.15)',
    '--csr-topbar':      '#020a04',
    '--csr-sidebar-bg':          '#020a04',
    '--csr-sidebar-heading':     '#00c272',
    '--csr-sidebar-text':        '#e4f5ec',
    '--csr-sidebar-active-bg':   '#00c272',
    '--csr-sidebar-active-text': '#ffffff',
    '--csr-sidebar-hover-text':  '#00c272',
    '--csr-sidebar-submenu-bg':  '#061509',
    '--csr-header-logo-filter':  'brightness(0) invert(1)',
    '--csr-sidebar-icon-filter': 'brightness(0) invert(1)',
  },
  purple: {
    '--csr-bg':          '#0d0a1a',
    '--csr-surface':     '#120f24',
    '--csr-surface2':    '#18152e',
    '--csr-panel':       '#120f24',
    '--csr-input':       '#18152e',
    '--csr-border':      '#2d2448',
    '--csr-border2':     '#1e1838',
    '--csr-text':        '#f0ecff',
    '--csr-text2':       '#9b8fc0',
    '--csr-text3':       '#5a4f7a',
    '--csr-muted':       '#9b8fc0',
    '--csr-accent':      '#a855f7',
    '--csr-accent-h':    '#c084fc',
    '--csr-accent-glow': 'rgba(168,85,247,0.15)',
    '--csr-topbar':      '#080614',
    '--csr-sidebar-bg':          '#080614',
    '--csr-sidebar-heading':     '#a855f7',
    '--csr-sidebar-text':        '#f0ecff',
    '--csr-sidebar-active-bg':   '#a855f7',
    '--csr-sidebar-active-text': '#ffffff',
    '--csr-sidebar-hover-text':  '#a855f7',
    '--csr-sidebar-submenu-bg':  '#120f24',
    '--csr-header-logo-filter':  'brightness(0) invert(1)',
    '--csr-sidebar-icon-filter': 'brightness(0) invert(1)',
  },
  default: {
    '--csr-bg':          '#f5f6fa',
    '--csr-surface':     '#ffffff',
    '--csr-surface2':    '#f4f6fa',
    '--csr-panel':       '#ffffff',
    '--csr-input':       '#ffffff',
    '--csr-border':      '#e0e0e0',
    '--csr-border2':     '#eeeeee',
    '--csr-text':        '#212121',
    '--csr-text2':       '#616161',
    '--csr-text3':       '#9e9e9e',
    '--csr-muted':       '#7a8aa2',
    '--csr-accent':      '#04a251',
    '--csr-accent-h':    '#038a44',
    '--csr-accent-glow': 'rgba(4,162,81,0.15)',
    '--csr-topbar':      '#ffffff',
    '--csr-sidebar-bg':          '#ffffff',
    '--csr-sidebar-heading':     '#04a251',
    '--csr-sidebar-text':        '#212121',
    '--csr-sidebar-active-bg':   '#04a251',
    '--csr-sidebar-active-text': '#f7f7f7',
    '--csr-sidebar-hover-text':  '#04a251',
    '--csr-sidebar-submenu-bg':  '#f4f6fa',
    '--csr-header-logo-filter':  'none',
    '--csr-sidebar-icon-filter': 'none',
  },
};

export function applyCsrTheme(id: CsrTheme): void {
  const root = document.documentElement;
  if (id === 'default') {
    Object.entries(CSR_THEME_VARS.default).forEach(([k, v]) => root.style.setProperty(k, v));
    root.removeAttribute('data-csr-theme');
    localStorage.setItem('csr-theme', id);
    return;
  }

  Object.entries(CSR_THEME_VARS[id]).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute('data-csr-theme', id);
  localStorage.setItem('csr-theme', id);
}

export function restoreCsrTheme(): CsrTheme {
  const saved = localStorage.getItem('csr-theme') as CsrTheme | null;
  const id = (saved && CSR_THEMES.some(t => t.id === saved)) ? saved : 'default';
  applyCsrTheme(id);
  return id;
}
